import { useEffect, useMemo, useState } from 'react';
import { Modal, Select, InputNumber, Button, Tag, Checkbox, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckCircleFilled, SaveOutlined } from '@ant-design/icons';
import { blankLine, parseItemDesc, composeItemDesc } from './proformaHelpers';
import { itemsApi } from '../../masters/items/itemsApi';
import {
  LINE_TYPE, SPACER_MASTER, INTERLAYER_MASTER, GAS_FILL_OPTIONS,
  composeDguDesc, composeLamiDesc
} from './proformaNestingMasters';
import SmartItemDescCell from './SmartItemDescField';
import './proformaNestingModal.css';

// ════════════════════════════════════════════════════════════════════
// DGU (double/triple glazed unit) config popup — alternating glass panes +
// spacers (+ gas fill), laid out as an Excel-style GRID (LAYER · ITEM DESC ·
// RATE · FAB · SHAPE · TEMPLATE). A glass pane may be a LAMI pane (glass +
// interlayer + glass sub-build); its bonded plies render as indented child
// rows. Reused for LAMI+DGU.
//
// Every GLASS field (pane or nested lami ply) is the SAME single smart field as
// the proforma grid's ITEM DESC column (`SmartItemDescCell`) — one cell for the
// Item-Master glass search AND the `#process@rate/uom` tags.
//
// SHAPE / TEMPLATE are per-glass capture-only flags (persisted in
// nesting_config); their business logic is defined in a later stage.
// ════════════════════════════════════════════════════════════════════

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const mmOf = (name) => { const m = String(name || '').match(/^([\d.]+)\s*MM/i); return m ? parseFloat(m[1]) : 0; };
const glassPane = () => ({ layerType: 'GLASS', product: '', rawItemDesc: '', itemDesc: '', processPricing: [], price: null, isLami: false, lamiLayers: null, fabApplicable: true, shape: false, template: false, itemId: null, stockItemId: null, stockItemName: null, materialTypeCode: null, categoryCode: null, glassTypeCode: null, thicknessMm: null });
const spacerLayer = () => ({ layerType: 'SPACER', product: '', price: null, gasFill: 'DRY AIR' });
const lamiGlass = () => ({ type: 'GLASS', product: '', rawItemDesc: '', itemDesc: '', processPricing: [], price: null, fabApplicable: true, shape: false, template: false, itemId: null, stockItemId: null, stockItemName: null, materialTypeCode: null, categoryCode: null, glassTypeCode: null, thicknessMm: null });
const lamiSub = () => ([lamiGlass(), { type: 'INTERLAYER', product: '', price: null }, lamiGlass()]);
const defaultLayers = () => [glassPane(), spacerLayer(), glassPane()];

// Rebuild a glass field's derived data (product/itemDesc/processPricing) from the
// single smart field's raw string — the SAME parse the grid uses.
function glassFromRaw(raw, prev) {
  const p = parseItemDesc(raw || '');
  return { ...prev, product: p.base, rawItemDesc: raw || '', itemDesc: composeItemDesc(p.base, p.tags), processPricing: p.pricing };
}

// Restore a saved DGU layer (from nestingConfig) back to the working shape.
function restoreLayer(l) {
  if (String(l.layerType).toUpperCase() === 'SPACER') {
    return { layerType: 'SPACER', product: l.product || '', price: l.price != null ? l.price : null, gasFill: l.gasFill || 'DRY AIR' };
  }
  const rawOf = (x) => x.rawItemDesc || (x.processDesc ? `${x.product || ''} ${x.processDesc}`.trim() : (x.product || ''));
  return glassFromRaw(rawOf(l), {
    layerType: 'GLASS', price: l.price != null ? l.price : null, isLami: !!l.isLami,
    fabApplicable: l.fabApplicable !== false, shape: l.shape === true, template: l.template === true,
    itemId: l.itemId || null, stockItemId: l.stockItemId || null, stockItemName: l.stockItemName || null,
    materialTypeCode: l.materialTypeCode || null, categoryCode: l.categoryCode || null, glassTypeCode: l.glassTypeCode || null, thicknessMm: l.thicknessMm != null ? l.thicknessMm : null,
    lamiLayers: l.isLami && Array.isArray(l.lamiLayers)
      ? l.lamiLayers.map((s) => (String(s.type).toUpperCase() === 'INTERLAYER'
        ? { type: 'INTERLAYER', product: s.product || '', price: s.price != null ? s.price : null }
        : glassFromRaw(rawOf(s), { type: 'GLASS', price: s.price != null ? s.price : null, fabApplicable: s.fabApplicable !== false, shape: s.shape === true, template: s.template === true, itemId: s.itemId || null, stockItemId: s.stockItemId || null, stockItemName: s.stockItemName || null, materialTypeCode: s.materialTypeCode || null, categoryCode: s.categoryCode || null, glassTypeCode: s.glassTypeCode || null, thicknessMm: s.thicknessMm != null ? s.thicknessMm : null })))
      : null
  });
}

// Stock-reference for a glass pane, derived from a picked Item-Master row.
function stockRefOf(item) {
  if (!item) return { itemId: null, stockItemId: null, stockItemName: null };
  return {
    itemId: item.id || null,
    stockItemId: item.sourceStockItemId || item.id || null,
    stockItemName: item.sourceStockItemName || item.itemName || item.itemDesc || null
  };
}
function classifyOf(item, productName) {
  const thk = mmOf(productName);
  return {
    materialTypeCode: (item && item.materialTypeCode) || null,
    categoryCode: (item && item.itemCategoryCode) || null,
    glassTypeCode: (item && item.glassTypeCode) || null,
    thicknessMm: thk > 0 ? thk : null
  };
}

const labelOf = (opts, val) => { const o = (opts || []).find((x) => x.value === val); return o ? o.label : (val || null); };

export default function DguConfigModal({ open, onClose, row, lamiDgu, dropdowns, onConfirm }) {
  const [layers, setLayers] = useState(defaultLayers());
  const [desiccantVal, setDesiccantVal] = useState();
  const [primarySealVal, setPrimarySealVal] = useState();
  const [secondarySealVal, setSecondarySealVal] = useState();
  const [desiccantOpts, setDesiccantOpts] = useState([]);
  const [primarySealOpts, setPrimarySealOpts] = useState([]);
  const [secondarySealOpts, setSecondarySealOpts] = useState([]);

  const [spacerOptions, setSpacerOptions] = useState(
    () => SPACER_MASTER.map((m) => ({ value: m.name, label: m.name })));
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    itemsApi.listDguConsumables({ consumableKind: 'spacer', pageSize: 200 })
      .then((res) => {
        if (!alive) return;
        const rows = (res && res.rows) || [];
        if (rows.length) setSpacerOptions(rows.map((r) => ({ value: r.itemName, label: r.itemName })));
      })
      .catch(() => { /* keep the seeded fallback */ });
    const loadKind = (kind, setter) => itemsApi.listDguConsumables({ consumableKind: kind, pageSize: 200 })
      .then((res) => { if (!alive) return; setter(((res && res.rows) || []).map((r) => ({ value: r.itemId, label: r.itemName }))); })
      .catch(() => { /* leave empty */ });
    loadKind('desiccant', setDesiccantOpts);
    loadKind('primary_sealant', setPrimarySealOpts);
    loadKind('secondary_sealant', setSecondarySealOpts);
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const cfg = row && row.nestingConfig ? row.nestingConfig : null;
    const saved = cfg && cfg.dgu && Array.isArray(cfg.dgu.layers) ? cfg.dgu.layers : null;
    if (saved && saved.length >= 3) {
      setLayers(saved.map(restoreLayer));
      setDesiccantVal(cfg.desiccantItemId || undefined);
      setPrimarySealVal(cfg.primarySealantItemId || undefined);
      setSecondarySealVal(cfg.secondarySealantItemId || undefined);
    } else {
      setLayers(defaultLayers());
      setDesiccantVal(undefined); setPrimarySealVal(undefined); setSecondarySealVal(undefined);
    }
  }, [open, row]);

  const upd = (i, patch) => setLayers((p) => p.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const updLami = (i, si, patch) => setLayers((p) => p.map((l, j) => {
    if (j !== i) return l;
    const sub = (l.lamiLayers || lamiSub()).map((s, k) => (k === si ? { ...s, ...patch } : s));
    return { ...l, lamiLayers: sub };
  }));
  // Toggle a pane between plain glass and a laminated pane (preserves position).
  const setPaneLami = (i, isLami) => upd(i, isLami
    ? { isLami: true, lamiLayers: lamiSub(), product: '', rawItemDesc: '', itemDesc: '', processPricing: [], price: null }
    : { isLami: false, lamiLayers: null, product: '', rawItemDesc: '', itemDesc: '', processPricing: [], price: null });
  const addPane = () => setLayers((p) => p.concat([spacerLayer(), glassPane()]));
  const removePane = () => setLayers((p) => (p.length <= 3 ? p : p.slice(0, p.length - 2)));

  const glassCount = layers.filter((l) => l.layerType === 'GLASS').length;
  const spacerCount = layers.filter((l) => l.layerType === 'SPACER').length;
  const label = glassCount === 2 ? 'Double' : glassCount === 3 ? 'Triple' : glassCount === 4 ? 'Quad' : 'Multi';

  const errs = useMemo(() => {
    const e = [];
    if (glassCount < 2) e.push('Min 2 glass panes');
    if (spacerCount < 1) e.push('Min 1 spacer');
    layers.forEach((l, i) => {
      if (l.layerType === 'GLASS') {
        if (l.isLami) {
          const sub = l.lamiLayers || [];
          if (sub.length < 3 || sub.some((s) => !s.product)) e.push(`Pane ${i + 1}: LAMI layers incomplete`);
        } else if (!l.product) e.push(`Pane ${i + 1}: not selected`);
      } else if (!l.product) e.push(`Spacer ${i + 1}: not selected`);
    });
    return e;
  }, [layers, glassCount, spacerCount]);
  const valid = errs.length === 0;

  const totalPrice = useMemo(() => layers.reduce((s, l) => {
    if (l.isLami && l.lamiLayers) return s + l.lamiLayers.reduce((a, sl) => a + num(sl.price), 0);
    return s + num(l.price);
  }, 0), [layers]);
  const totalThk = useMemo(() => layers.reduce((s, l) => {
    if (l.isLami && l.lamiLayers) return s + l.lamiLayers.reduce((a, sl) => a + mmOf(sl.product), 0);
    return s + mmOf(l.product);
  }, 0), [layers]);
  // Narration carries each glass ply's process tags (pane + nested lami).
  const descLayers = useMemo(() => layers.map((l) => (
    l.lamiLayers
      ? { ...l, lamiLayers: l.lamiLayers.map((s) => ({ ...s, tags: (s.processPricing || []).map((p) => p.name) })) }
      : { ...l, tags: (l.processPricing || []).map((p) => p.name) }
  )), [layers]);
  const desc = useMemo(() => composeDguDesc(descLayers, label), [descLayers, label]);
  const isEdit = row && (String(row.type).toUpperCase() === LINE_TYPE.DGU || String(row.type).toUpperCase() === LINE_TYPE.LAMI_DGU);

  const handleOk = () => {
    if (!valid || !row) return;
    const lvl = num(row.level) + 1;
    const mk = (level, parentRef, type, l, priceOverride) => {
      const c = blankLine(1, level, parentRef);
      const isGlass = type === LINE_TYPE.GLASS;
      c.type = type;
      c.itemDesc = (isGlass ? (l.itemDesc || l.product) : l.product);
      c.rawItemDesc = (isGlass ? (l.rawItemDesc || l.product) : l.product);
      c.salesRate = num(priceOverride != null ? priceOverride : l.price); c.priceUomCode = row.priceUomCode || null; c.gstRate = row.gstRate;
      c.location = row.location || '';
      c.actualWidthMm = row.actualWidthMm; c.actualHeightMm = row.actualHeightMm; c.qty = row.qty;
      if (isGlass) {
        c.itemId = l.itemId || null; c.stockItemId = l.stockItemId || null; c.stockItemName = l.stockItemName || null;
        c.materialTypeCode = l.materialTypeCode || null; c.categoryCode = l.categoryCode || null;
        c.glassTypeCode = l.glassTypeCode || null; c.thicknessMm = l.thicknessMm != null ? l.thicknessMm : null;
        if (l.fabApplicable !== undefined) c.fabApplicable = l.fabApplicable !== false;
        // Per-ply SHAPE / TEMPLATE opt-in → this glass ply's own nesting_config so
        // the Other-Charges engine bills the Shape / Template charge on it (same as
        // a normal T-SHAPE / T-TEMPLATE line). Covers DGU panes AND nested-LAMI plies.
        c.nestingConfig = { ...(c.nestingConfig || {}), shape: l.shape === true, template: l.template === true };
        const proc = (l.processPricing || []).filter((p) => p.rate > 0);
        if (proc.length) c.processPricing = proc;
      }
      return c;
    };
    // Simple text child (spacer / gas) — no glass fields.
    const mkText = (level, parentRef, type, text, price) => {
      const c = blankLine(1, level, parentRef);
      c.type = type; c.itemDesc = text; c.rawItemDesc = text; c.salesRate = num(price);
      c.priceUomCode = row.priceUomCode || null; c.gstRate = row.gstRate; c.location = row.location || '';
      c.actualWidthMm = row.actualWidthMm; c.actualHeightMm = row.actualHeightMm; c.qty = row.qty;
      return c;
    };
    const block = [];
    for (const l of layers) {
      if (l.layerType === 'GLASS' && l.isLami && l.lamiLayers) {
        const sub = l.lamiLayers.filter((s) => s.product);
        const lamiPrice = sub.reduce((a, s) => a + num(s.price), 0);
        const parentLami = mkText(lvl, row.refUuid, LINE_TYPE.LAMI,
          composeLamiDesc(sub.map((s) => ({ product: s.product, tags: (s.processPricing || []).map((p) => p.name) }))), lamiPrice);
        block.push(parentLami);
        for (const s of sub) {
          const isGlass = s.type === 'GLASS';
          block.push(isGlass
            ? mk(lvl + 1, parentLami.refUuid, LINE_TYPE.GLASS, s)
            : mkText(lvl + 1, parentLami.refUuid, LINE_TYPE.INTERLAYER, s.product, s.price));
        }
      } else if (l.layerType === 'GLASS') {
        block.push(mk(lvl, row.refUuid, LINE_TYPE.GLASS, l));
      } else {
        block.push(mkText(lvl, row.refUuid, LINE_TYPE.SPACER, l.product, l.price));
        block.push(mkText(lvl, row.refUuid, LINE_TYPE.GASFILL, l.gasFill || 'DRY AIR', 0));
      }
    }
    const parentType = lamiDgu ? LINE_TYPE.LAMI_DGU : LINE_TYPE.DGU;
    const glassCfg = (l) => ({
      layerType: 'GLASS', product: l.product || '', price: num(l.price), isLami: !!l.isLami,
      rawItemDesc: l.rawItemDesc || '', processPricing: l.processPricing || [],
      fabApplicable: l.fabApplicable !== false, shape: l.shape === true, template: l.template === true,
      itemId: l.itemId || null, stockItemId: l.stockItemId || null, stockItemName: l.stockItemName || null,
      materialTypeCode: l.materialTypeCode || null, categoryCode: l.categoryCode || null, glassTypeCode: l.glassTypeCode || null, thicknessMm: l.thicknessMm != null ? l.thicknessMm : null,
      lamiLayers: l.isLami && Array.isArray(l.lamiLayers)
        ? l.lamiLayers.map((s) => (String(s.type).toUpperCase() === 'INTERLAYER'
          ? { type: 'INTERLAYER', product: s.product || '', price: num(s.price) }
          : { type: 'GLASS', product: s.product || '', price: num(s.price), fabApplicable: s.fabApplicable !== false, shape: s.shape === true, template: s.template === true, rawItemDesc: s.rawItemDesc || '', processPricing: s.processPricing || [], itemId: s.itemId || null, stockItemId: s.stockItemId || null, stockItemName: s.stockItemName || null, materialTypeCode: s.materialTypeCode || null, categoryCode: s.categoryCode || null, glassTypeCode: s.glassTypeCode || null, thicknessMm: s.thicknessMm != null ? s.thicknessMm : null })) : null
    });
    onConfirm({
      type: parentType, itemDesc: desc, rawItemDesc: desc, salesRate: totalPrice,
      isExpanded: false,
      nestingConfig: {
        ...(row.nestingConfig || {}),
        desiccantItemId: desiccantVal || null,
        desiccantName: labelOf(desiccantOpts, desiccantVal),
        primarySealantItemId: primarySealVal || null,
        primarySealantName: labelOf(primarySealOpts, primarySealVal),
        secondarySealantItemId: secondarySealVal || null,
        secondarySealantName: labelOf(secondarySealOpts, secondarySealVal),
        sealant: labelOf(primarySealOpts, primarySealVal) || (row.nestingConfig && row.nestingConfig.sealant) || null,
        desiccant: labelOf(desiccantOpts, desiccantVal) || (row.nestingConfig && row.nestingConfig.desiccant) || null,
        dgu: {
          layers: layers.map((l) => (String(l.layerType).toUpperCase() === 'SPACER'
            ? { layerType: 'SPACER', product: l.product || '', price: num(l.price), gasFill: l.gasFill || 'DRY AIR' }
            : glassCfg(l)))
        }
      }
    }, block);
  };

  // ── glass smart field (pane or nested lami ply) ──
  const glassField = (cellId, refUuid, l, onCommit, onPickItem) => (
    <SmartItemDescCell
      cellId={cellId} size="small" dropdowns={dropdowns}
      row={{ refUuid, rawItemDesc: l.rawItemDesc || '', itemDesc: l.itemDesc || l.product || '', processPricing: l.processPricing || [] }}
      onCommit={onCommit} onPickItem={onPickItem} onPickCombo={() => {}} onRftAdded={() => {}} />
  );
  const pickGlass = (item) => {
    const patch = { ...stockRefOf(item), ...classifyOf(item, item.itemDesc || item.itemName) };
    if (item.salesRate != null) patch.price = Number(item.salesRate);
    return patch;
  };

  // A glass ply checkbox cell (FAB / SHAPE / TEMPLATE).
  const chkCell = (checked, onChange, tip) => (
    <td className="gi-lg-chkcell">
      <Tooltip title={tip}><Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} /></Tooltip>
    </td>
  );
  const FAB_TIP = 'Apply the parent fabrication (holes / cut-outs) to this glass ply. Untick to exclude it.';
  const SHAPE_TIP = 'This glass ply is shaped (non-rectangular). Business logic is defined in a later stage.';
  const TMPL_TIP = 'This glass ply needs a physical template. Business logic is defined in a later stage.';

  // ── grid rows (pane / nested lami plies / spacer) ──
  const rows = [];
  layers.forEach((l, i) => {
    if (l.layerType === 'SPACER') {
      rows.push(
        <tr key={`sp-${i}`}>
          <td><span className="gi-lg-static">SPACER</span></td>
          <td>
            <Select size="small" style={{ width: '100%' }} showSearch value={l.product || undefined} placeholder="Choose spacer"
              optionFilterProp="label" onChange={(v) => upd(i, { product: v })} options={spacerOptions} />
            <div className="gi-lg-gasrow">
              <span>Gas</span>
              <Select size="small" value={l.gasFill || 'DRY AIR'} onChange={(v) => upd(i, { gasFill: v })}
                options={GAS_FILL_OPTIONS.map((g) => ({ value: g, label: g }))} />
            </div>
          </td>
          <td>
            <InputNumber size="small" style={{ width: '100%' }} min={0} controls={false} prefix="₹" placeholder="rate"
              value={l.price} onChange={(val) => upd(i, { price: val })} />
          </td>
          <td className="gi-lg-chkcell" /><td className="gi-lg-chkcell" /><td className="gi-lg-chkcell" />
        </tr>
      );
      return;
    }
    // glass pane (plain or LAMI pane parent)
    rows.push(
      <tr key={`pane-${i}`}>
        <td>
          <Select size="small" style={{ width: '100%' }} value={l.isLami ? 'LAMI' : 'GLASS'}
            onChange={(v) => setPaneLami(i, v === 'LAMI')}
            options={[{ value: 'GLASS', label: 'GLASS PANE' }, { value: 'LAMI', label: 'LAMI PANE' }]} />
        </td>
        <td>
          {l.isLami
            ? <div className="gi-lg-panehint">Laminated pane — plies below</div>
            : glassField(`dgu-pane-${i}`, `dgu-${i}`, l, (patch) => upd(i, glassFromRaw(patch.rawItemDesc || '', l)), (item) => upd(i, pickGlass(item)))}
        </td>
        <td>
          {l.isLami
            ? <div className="gi-lg-rate-ro">₹ {(l.lamiLayers || []).reduce((a, s) => a + num(s.price), 0) || 0}</div>
            : <InputNumber size="small" style={{ width: '100%' }} min={0} controls={false} prefix="₹" placeholder="rate" value={l.price} onChange={(val) => upd(i, { price: val })} />}
        </td>
        {l.isLami
          ? (<><td className="gi-lg-chkcell" /><td className="gi-lg-chkcell" /><td className="gi-lg-chkcell" /></>)
          : (<>
            {chkCell(l.fabApplicable !== false, (c) => upd(i, { fabApplicable: c }), FAB_TIP)}
            {chkCell(l.shape === true, (c) => upd(i, { shape: c }), SHAPE_TIP)}
            {chkCell(l.template === true, (c) => upd(i, { template: c }), TMPL_TIP)}
          </>)}
      </tr>
    );
    if (l.isLami) {
      (l.lamiLayers || lamiSub()).forEach((s, si) => {
        const isG = s.type === 'GLASS';
        rows.push(
          <tr key={`pane-${i}-ply-${si}`} className="gi-lg-child">
            <td><span className="gi-lg-child-layer">{isG ? 'Glass' : 'Interlayer'}</span></td>
            <td>
              {isG
                ? glassField(`dgu-lami-${i}-${si}`, `dgu-${i}-${si}`, s, (patch) => updLami(i, si, glassFromRaw(patch.rawItemDesc || '', s)), (item) => updLami(i, si, pickGlass(item)))
                : <Select size="small" style={{ width: '100%' }} showSearch value={s.product || undefined} placeholder="Select interlayer"
                    optionFilterProp="label" onChange={(v) => updLami(i, si, { product: v })} options={INTERLAYER_MASTER.map((m) => ({ value: m.name, label: m.name }))} />}
            </td>
            <td>
              <InputNumber size="small" style={{ width: '100%' }} min={0} controls={false} prefix="₹" placeholder="rate" value={s.price} onChange={(val) => updLami(i, si, { price: val })} />
            </td>
            {isG
              ? (<>
                {chkCell(s.fabApplicable !== false, (c) => updLami(i, si, { fabApplicable: c }), FAB_TIP)}
                {chkCell(s.shape === true, (c) => updLami(i, si, { shape: c }), SHAPE_TIP)}
                {chkCell(s.template === true, (c) => updLami(i, si, { template: c }), TMPL_TIP)}
              </>)
              : (<><td className="gi-lg-chkcell" /><td className="gi-lg-chkcell" /><td className="gi-lg-chkcell" /></>)}
          </tr>
        );
      });
    }
  });

  return (
    <Modal title={`${isEdit ? 'Edit' : 'Build'} ${label} Glazed Unit${lamiDgu ? ' (Laminated)' : ''}`}
      open={open} onCancel={onClose} onOk={handleOk} className="gi-nest-modal"
      okText={isEdit ? 'Save Changes' : `Create ${lamiDgu ? 'LAMI+DGU' : 'DGU'}`}
      okButtonProps={{ disabled: !valid, type: 'primary', icon: <SaveOutlined /> }}
      width={820} centered destroyOnClose maskClosable={false}>

      <p className="text-[12px] text-slate-500 -mt-1 mb-3">
        Glass panes separated by <b>spacers</b> (with gas fill). Set a pane's LAYER to <b>LAMI PANE</b> to laminate it —
        its plies appear as indented rows. In <b>ITEM DESC</b> type the glass, then add <b>#process@rate/uom</b> tags in the same cell.
      </p>

      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] font-semibold text-slate-700">Panes &amp; spacers (outside → inside)</span>
        <div className="flex items-center gap-1.5">
          <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addPane}>Add pane</Button>
          {glassCount > 2 ? <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={removePane}>Remove pane</Button> : null}
        </div>
      </div>

      {/* ── Excel-style layer grid (panes + spacers + nested lami plies) ── */}
      <table className="gi-lg">
        <colgroup>
          <col className="gi-lg-layer" /><col /><col className="gi-lg-rate" />
          <col className="gi-lg-chk" /><col className="gi-lg-chk" /><col className="gi-lg-tmpl" />
        </colgroup>
        <thead>
          <tr><th>LAYER</th><th>ITEM DESC</th><th>RATE</th><th>FAB</th><th>SHAPE</th><th>TEMPLATE</th></tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>

      {/* Edge-sealing consumables — explicit item picks from the DGU master. */}
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-3 flex-wrap">
        <span className="text-[11px] font-semibold text-slate-500">Edge sealing</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-slate-400">Desiccant</span>
          <Select size="small" allowClear showSearch optionFilterProp="label" placeholder="—"
            value={desiccantVal} onChange={setDesiccantVal} options={desiccantOpts} style={{ width: 180 }} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-slate-400">Primary sealant</span>
          <Select size="small" allowClear showSearch optionFilterProp="label" placeholder="—"
            value={primarySealVal} onChange={setPrimarySealVal} options={primarySealOpts} style={{ width: 180 }} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-slate-400">Secondary sealant</span>
          <Select size="small" allowClear showSearch optionFilterProp="label" placeholder="—"
            value={secondarySealVal} onChange={setSecondarySealVal} options={secondarySealOpts} style={{ width: 180 }} />
        </div>
      </div>

      {/* validity + totals */}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          {valid
            ? <span className="flex items-center gap-1.5 text-[12px] text-emerald-600 font-medium"><CheckCircleFilled /> Ready — this is a valid {label.toLowerCase()} unit.</span>
            : (
              <ul className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 m-0 list-disc pl-6 space-y-0.5">
                {errs.map((e, k) => <li key={k}>{e}</li>)}
              </ul>
            )}
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <Tag color="geekblue" className="!m-0">{glassCount} panes</Tag>
          <Tag className="!m-0">{spacerCount} spacer{spacerCount === 1 ? '' : 's'}</Tag>
          <Tag color="purple" className="!m-0">{label}</Tag>
          <div className="flex items-baseline gap-1 pl-1">
            <span className="text-[16px] leading-none font-bold text-slate-800 tabular-nums">{totalThk.toFixed(2)}</span>
            <span className="text-[11px] font-semibold text-slate-400">mm</span>
          </div>
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1 flex items-baseline gap-1.5">
            <span className="text-[10px] text-emerald-600 uppercase tracking-wide">Rate / area</span>
            <span className="text-[14px] font-bold text-emerald-700 tabular-nums">₹ {totalPrice.toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>

      {/* Full product narration (the line that lands on the proforma row). */}
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Product narration</div>
        <div className="text-[12.5px] font-medium text-slate-800 leading-snug break-words">{desc || '—'}</div>
      </div>
    </Modal>
  );
}
